'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useTranslations, useLocale } from 'next-intl';

/**
 * Галерея снимков «Конспекта».
 *
 * Четыре снимка в сетке читались как документация: человек видел четыре
 * мелких картинки сразу и не смотрел ни на одну. Здесь показывается один
 * снимок крупно, а остальные выбираются вкладками с названиями экранов —
 * так видно, что именно приложение умеет, и снимок можно рассмотреть.
 *
 * Снимки берутся из папки своего языка: интерфейс приложения переведён, и
 * английские скриншоты на русской странице выглядят как чужой продукт.
 *
 * Листание идёт само, но останавливается навсегда, как только человек
 * выбрал экран сам: дальше он рассматривает, а уезжающая из-под рук
 * картинка этому мешает. Наведение мыши тоже приостанавливает показ.
 *
 * Промо-ролик (02.10.2026, просьба владельца) — необязательная первая
 * вкладка «Ролик». Пока он идёт, автолистание стоит: уехавший посреди
 * ролика экран обрывал бы показ. Кончился ролик — галерея сама переходит
 * к снимкам и листает дальше как раньше.
 */
const SHOTS = ['summary', 'transcript', 'notes', 'dictation'] as const;

/** Сколько показывается один снимок, пока человек не вмешался. */
const INTERVAL_MS = 5000;

export interface Rolik {
  /** Видео mp4 (h264, faststart), путь от корня сайта. */
  src: string;
  /** Кадр-заставка до загрузки видео. */
  poster: string;
}

export function KonspektGallery({ rolik }: { rolik?: Rolik } = {}) {
  const t = useTranslations('ais_audio');
  const locale = useLocale();

  const tabs: string[] = rolik ? ['rolik', ...SHOTS] : [...SHOTS];

  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  // Выбор руками отменяет автолистание совсем, а не на один шаг: человек
  // уже сказал, что хочет смотреть именно этот экран.
  const [stopped, setStopped] = useState(false);
  const region = useRef<HTMLDivElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const naRolike = tabs[active] === 'rolik';

  useEffect(() => {
    // На ролике листание ждёт его конца (onEnded ниже).
    if (stopped || paused || naRolike) return;

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (motion.matches) return;

    const timer = setInterval(() => {
      // По кругу только снимки: ролик второй раз сам не включается.
      setActive((i) => {
        const sled = i + 1;
        if (sled < tabs.length) return sled;
        return rolik ? 1 : 0;
      });
    }, INTERVAL_MS);

    return () => {
      clearInterval(timer);
    };
  }, [paused, stopped, naRolike, tabs.length, rolik]);

  // Ушли с вкладки ролика — ставим его на паузу, вернулись — играем.
  useEffect(() => {
    const v = video.current;
    if (!v) return;
    if (naRolike) void v.play().catch(() => undefined);
    else v.pause();
  }, [naRolike]);

  function choose(index: number) {
    setActive(index);
    setStopped(true);
  }

  return (
    <div
      ref={region}
      onMouseEnter={() => {
        setPaused(true);
      }}
      onMouseLeave={() => {
        setPaused(false);
      }}
    >
      {/* Вкладки названы экранами приложения, а не «1 2 3 4»: по подписи
          видно, что показывают, ещё до переключения. */}
      <div
        role="tablist"
        aria-label={t('app_shots_title')}
        className="mb-5 flex flex-wrap justify-center gap-2"
      >
        {tabs.map((shot, index) => (
          <button
            key={shot}
            type="button"
            role="tab"
            aria-selected={index === active}
            aria-controls={`konspekt-shot-${shot}`}
            onClick={() => {
              choose(index);
            }}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              index === active
                ? 'bg-[var(--color-accent-500)] text-white'
                : 'border border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-accent-500)] hover:text-[var(--color-text)]'
            }`}
          >
            {t(`app_tab_${shot}`)}
          </button>
        ))}
      </div>

      {/* Рамка вокруг снимка: экран приложения на странице сайта иначе
          сливается с фоном, потому что тема у них одна и та же тёмная. */}
      <div className="overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg)] shadow-2xl">
        <div className="relative aspect-[16/10]">
          {rolik && (
            <div
              id="konspekt-shot-rolik"
              role="tabpanel"
              aria-label={t('app_shot_rolik')}
              className={`absolute inset-0 flex items-center bg-black transition-opacity duration-500 ${
                naRolike ? 'opacity-100' : 'pointer-events-none opacity-0'
              }`}
            >
              {/* Без звука и с playsInline — иначе мобильные браузеры
                  не запускают видео сами; звук включается в controls. */}
              <video
                ref={video}
                src={rolik.src}
                poster={rolik.poster}
                autoPlay
                muted
                playsInline
                controls
                preload="metadata"
                onEnded={() => {
                  if (!stopped) setActive(1);
                }}
                className="h-full w-full object-contain"
              />
            </div>
          )}
          {SHOTS.map((shot) => {
            const index = tabs.indexOf(shot);
            return (
              <div
                key={shot}
                id={`konspekt-shot-${shot}`}
                role="tabpanel"
                aria-label={t(`app_shot_${shot}`)}
                // Снимки лежат друг на друге и меняются прозрачностью:
                // сдвиг или подмена источника давали бы белое мигание,
                // потому что следующая картинка грузится не мгновенно.
                className={`absolute inset-0 transition-opacity duration-500 ${
                  index === active ? 'opacity-100' : 'pointer-events-none opacity-0'
                }`}
              >
                <Image
                  src={`/images/konspekt/${locale}/${shot}.png`}
                  alt={t(`app_shot_${shot}`)}
                  fill
                  sizes="(max-width: 768px) 100vw, 900px"
                  // Первый снимок нужен сразу, остальные подгружаются
                  // фоном: они всё равно окажутся нужны через пять секунд.
                  priority={index === 0}
                  className="object-cover object-top"
                />
              </div>
            );
          })}
        </div>
      </div>

      <p className="mt-3 text-center text-sm text-[var(--color-text-muted)]">
        {t(`app_shot_${tabs[active] ?? 'summary'}`)}
      </p>
    </div>
  );
}
